import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { Input, Textarea, AttachmentUpload } from 'components/FormFields';
import Toaster from 'components/Toaster';
import { useGetEventForEdit } from '../EditEventV2/gql/useGetEventForEdit';
import { useUpdateEventInfo } from './hooks/useUpdateEventInfo';

// ============================================================================
// SVG Icons
// ============================================================================
const LocationIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const ClipboardIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);

// ============================================================================
// Styled Components
// ============================================================================

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme?.colors?.background?.primary ||
    (theme?.name === 'dark' ? '#1a1a2e' : '#f8fafc')};
  font-family: ${({ theme }) => theme?.fonts?.primary};
`;

const Container = styled.div`
  padding: 30px;
  max-width: 900px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`;

// Date display card - matches design system (same as EditEventV2)
const DateDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fa4616;
  color: white;
  border-radius: 8px;
  padding: 10px 16px;
  min-width: 70px;
  gap: 4px;
`;

const DatePill = styled.div`
  background: white;
  color: #1e293b;
  font-size: 14px;
  font-weight: 800;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  letter-spacing: 0.5px;
`;

const DateText = styled.div`
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
`;

const EventInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Title = styled.h1`
  font-size: 27px;
  font-weight: 700;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  margin: 0 0 4px 0;

  @media (max-width: 768px) {
    font-size: 23px;
  }

  @media (max-width: 480px) {
    font-size: 21px;
  }
`;

const EventMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: ${({ theme }) => theme?.colors?.textGray?.secondary || '#94A3B8'};
  font-size: 17px;

  span {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    font-size: 16px;
  }
`;

const HeaderButton = styled.div`
  width: auto;
  flex: ${({ fullWidth }) => (fullWidth ? 1 : 'none')};

  button {
    width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
    padding: 10px 24px;
  }

  @media (max-width: 768px) {
    flex: 1;

    button {
      width: 100%;
    }
  }
`;

const ContentCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background || 'rgba(255, 255, 255, 0.03)'};
  border-radius: ${({ theme }) => theme?.borderRadius?.lg || '12px'};
  border: 1px solid
    ${({ theme }) => theme?.colors?.card?.border || 'rgba(255, 255, 255, 0.08)'};
  padding: 32px;

  @media (max-width: 768px) {
    padding: 24px 20px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid
    ${({ theme }) => theme?.colors?.border || 'rgba(255, 255, 255, 0.08)'};
`;

const CardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme?.colors?.primary || '#fa4616'};
`;

const CardTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  margin: 0;
`;

const FormSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme?.colors?.text?.primary || '#ffffff'};
  margin-bottom: 8px;
`;

const FormHint = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme?.colors?.textGray?.muted || '#64748B'};
  margin: 8px 0 0 0;
`;

// ============================================================================
// Helper Functions
// ============================================================================

// Helper to parse date for display in DateBox
const parseDateForDisplay = (startDateString, endDateString) => {
  if (!startDateString) return null;

  // Parse date - handle both MM-DD-YYYY and YYYY-MM-DD formats
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts[0].length === 4) {
      // YYYY-MM-DD
      return new Date(parts[0], parts[1] - 1, parts[2]);
    } else {
      // MM-DD-YYYY
      return new Date(parts[2], parts[0] - 1, parts[1]);
    }
  };

  const startDate = parseDate(startDateString);
  if (!startDate || isNaN(startDate.getTime())) return null;

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Check if multi-day event
  let isMultiDay = false;
  let endDay = null;

  if (endDateString && endDateString !== startDateString) {
    const endDate = parseDate(endDateString);
    if (endDate && !isNaN(endDate.getTime())) {
      isMultiDay = true;
      endDay = endDate.getDate();
    }
  }

  return {
    month: months[startDate.getMonth()],
    day: startDate.getDate(),
    dayOfWeek: days[startDate.getDay()],
    year: startDate.getFullYear(),
    isMultiDay,
    endDay,
  };
};

// ============================================================================
// Component
// ============================================================================

const CreateEventFinishContent = () => {
  const history = useHistory();
  const location = useLocation();
  const { eventId } = useParams();

  // Fetch event data
  const { event, loading: eventLoading } = useGetEventForEdit(eventId);

  // Update mutation
  const { updateEventInfo, loading: saving } = useUpdateEventInfo();

  // Form state
  const [formValues, setFormValues] = useState({
    schedule_detail_image: [],
    schedule_detail_url: '',
    schedule_details: '',
    about: '',
  });

  // Toaster state
  const [toaster, setToaster] = useState({ isOpen: false });

  // Initialize form values from event data
  useEffect(() => {
    if (event) {
      const attachments = [];
      if (event.schedule_detail_image || event.schedule_detail_image_id) {
        attachments.push({
          id: event.schedule_detail_image_id || 'existing',
          name: 'Schedule Image',
          size: 0,
          sizeFormatted: '',
          url: event.schedule_detail_image || '',
          type: 'image/jpeg',
          isImage: true,
        });
      }

      setFormValues({
        schedule_detail_image: attachments,
        schedule_detail_url: event.schedule_detail_url || '',
        schedule_details: event.schedule_details || '',
        about: event.about || '',
      });
    }
  }, [event]);

  // Determine route prefix for context-aware navigation
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const handleBack = () => {
    history.push(`${routePrefix}/events/addEvent/${eventId}/tickets`);
  };

  const handleFinish = async () => {
    try {
      // Build the update input
      const input = {
        event_id: parseInt(eventId),
        about: formValues.about,
        schedule_details: formValues.schedule_details,
        schedule_detail_url: formValues.schedule_detail_url,
      };

      // Handle image - get the ID from attachments
      if (formValues.schedule_detail_image.length > 0) {
        const attachment = formValues.schedule_detail_image[0];
        if (attachment.id && attachment.id !== 'existing') {
          input.schedule_detail_image_id = parseInt(attachment.id);
        }
      } else {
        // Clear the image if removed
        input.schedule_detail_image_id = null;
      }

      await updateEventInfo(input);

      setToaster({
        isOpen: true,
        type: 'success',
        title: 'Event Created!',
        message: 'Your event has been set up successfully.',
      });

      // Navigate after short delay to show toaster
      setTimeout(() => {
        history.push(`${routePrefix}/events/editEvent/${eventId}`);
      }, 1000);
    } catch (error) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to save event details. Please try again.',
      });
    }
  };

  const handleChange = (field) => (e) => {
    try {
      const value = e?.target?.value ?? '';
      setFormValues((prev) => ({
        ...prev,
        [field]: value,
      }));
    } catch (err) {
      // Defensive catch for paste edge cases
      console.warn('handleChange error:', err);
    }
  };

  const handleAttachmentsChange = (attachments) => {
    setFormValues((prev) => ({
      ...prev,
      schedule_detail_image: attachments,
    }));
  };

  if (eventLoading) {
    return (
      <PageWrapper>
        <Container>
          <div
            style={{ textAlign: 'center', padding: '60px', color: '#94A3B8' }}
          >
            Loading event...
          </div>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <HeaderLeft>
            {(() => {
              const dateInfo = parseDateForDisplay(
                event?.start_date,
                event?.end_date
              );
              if (!dateInfo) return null;
              return (
                <DateDisplay>
                  {dateInfo.isMultiDay ? (
                    <>
                      <DatePill>{dateInfo.month}</DatePill>
                      <DateText>
                        {dateInfo.day} - {dateInfo.endDay}
                      </DateText>
                    </>
                  ) : (
                    <>
                      <DatePill>{dateInfo.dayOfWeek}</DatePill>
                      <DateText>
                        {dateInfo.month} {dateInfo.day}
                      </DateText>
                    </>
                  )}
                </DateDisplay>
              );
            })()}
            <EventInfo>
              <Title>{event?.name || 'Finish Setup'}</Title>
              <EventMeta>
                {event?.track?.name && (
                  <span>
                    <LocationIcon />
                    {event.track.name}
                  </span>
                )}
              </EventMeta>
            </EventInfo>
          </HeaderLeft>
          <HeaderRight>
            <HeaderButton>
              <ButtonV2 type="button" onClick={handleBack}>
                Back
              </ButtonV2>
            </HeaderButton>
            <HeaderButton>
              <ButtonV2
                elevated
                type="button"
                onClick={handleFinish}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Finish'}
              </ButtonV2>
            </HeaderButton>
          </HeaderRight>
        </PageHeader>

        <ContentCard>
          <CardHeader>
            <CardIcon>
              <ClipboardIcon />
            </CardIcon>
            <CardTitle>Schedule Details</CardTitle>
          </CardHeader>

          <FormSection>
            <FormLabel>Schedule Details Image</FormLabel>
            <AttachmentUpload
              attachments={formValues.schedule_detail_image}
              onAttachmentsChange={handleAttachmentsChange}
              maxFiles={1}
              channel="SMS"
            />
            <FormHint>Upload a schedule image (JPG, PNG, or PDF)</FormHint>
          </FormSection>

          <FormSection>
            <Input
              label="Schedule Details Link"
              placeholder="https://example.com/schedule"
              value={formValues.schedule_detail_url}
              onChange={handleChange('schedule_detail_url')}
            />
            <FormHint>Link to an external schedule page</FormHint>
          </FormSection>

          <FormSection>
            <Textarea
              label="Schedule Details"
              placeholder="Enter schedule details..."
              value={formValues.schedule_details}
              onChange={handleChange('schedule_details')}
              rows={6}
            />
            <FormHint>Detailed schedule information for the event</FormHint>
          </FormSection>
        </ContentCard>

        <ContentCard style={{ marginTop: '24px' }}>
          <CardHeader>
            <CardIcon>
              <InfoIcon />
            </CardIcon>
            <CardTitle>Event Info</CardTitle>
          </CardHeader>

          <FormSection>
            <Textarea
              label="Event Info"
              placeholder="Enter event information..."
              value={formValues.about}
              onChange={handleChange('about')}
              rows={8}
            />
            <FormHint>General information about the event</FormHint>
          </FormSection>
        </ContentCard>
      </Container>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </PageWrapper>
  );
};

const CreateEventFinish = () => {
  return (
    <EnhancedThemeProvider>
      <CreateEventFinishContent />
    </EnhancedThemeProvider>
  );
};

export default CreateEventFinish;
